// Square Code V2 Agent Command — local design tokens ("neon void").
//
// Extracted from the approved design-reference prototypes
// (SQ_TeamControl.html, SQ_RecursiveMatrix.html): near-black void, an
// upper-right radial atmosphere, a fine 48px grid overlay, Chakra Petch
// display type, JetBrains Mono labels, and small status-chip pills.
//
// These are LOCAL tokens scoped to this one page — not additions to
// packages/square-ui/src/tokens.css. The page's status-accent mapping is
// intentionally its own vocabulary (brighter neon values) rather than the
// shared WORKSPACE_ACCENTS system the rest of the command center uses, per
// the approved design language for this surface.
//
// Status-accent mapping is REQUIRED to be exactly this (not the reference
// prototype's violet-led mapping):
//   green  -> runtime_ready / powered / active
//   cyan   -> provider-backed
//   violet -> review_required / needs-approval
//   amber  -> setup_required / provider-blocked
//   red    -> failure / denied

import type { CSSProperties } from 'react'

export const AGENT_VOID = {
  bg: '#04070a',
  ink: '#f2f4ff',
  muted: 'var(--sc-text-secondary, #b4bfd8)',
  faint: 'var(--sc-text-tertiary, #8b93ad)',
  hairline: 'rgba(150, 140, 220, 0.14)',
} as const

// Required status-accent mapping (exact hex, so chip/tag alpha composition
// via string concatenation stays valid — never pass a CSS var through this).
export const AGENT_STATUS_ACCENT = {
  green: '#3ff0a8', // runtime_ready / powered / active
  cyan: '#22d3ee', // provider-backed
  violet: '#a577ff', // review_required / needs-approval
  amber: '#ffab38', // setup_required / provider-blocked
  red: '#ff5c5c', // failure / denied
  unknown: '#8b93ad', // honest neutral — no known-good/known-bad claim
} as const

// Page identity accent — matches the Final Hub "Agents" quick-access tile
// (commandCenterHubModel.ts QUICK_ACCENT.agents = '#38b6ff') so the tile and
// the page it opens share one identity color. Decorative only (kicker, h1
// glow, section-label rule) — status chips/tags always use
// AGENT_STATUS_ACCENT, never this.
export const AGENT_IDENTITY_ACCENT = '#38b6ff'

export const AGENT_FONT = {
  display: '"Chakra Petch", "Instrument Sans", system-ui, sans-serif',
  mono: '"JetBrains Mono", var(--sc-font-mono), ui-monospace, monospace',
  body: 'Inter, var(--sc-font-ui), system-ui, sans-serif',
} as const

// Near-black void + upper-right cyan/green radial atmosphere (the project's
// own accent mapping — deliberately NOT the reference's violet atmosphere)
// + a fine 48px grid overlay at ~3% opacity.
export const AGENT_VOID_BACKGROUND: CSSProperties = {
  backgroundColor: AGENT_VOID.bg,
  backgroundImage: [
    'radial-gradient(900px 500px at 88% -10%, rgba(34,211,238,0.10), transparent)',
    'radial-gradient(720px 460px at 4% 106%, rgba(63,240,168,0.07), transparent)',
    'linear-gradient(rgba(150,140,220,0.03) 1px, transparent 1px)',
    'linear-gradient(90deg, rgba(150,140,220,0.03) 1px, transparent 1px)',
  ].join(', '),
  backgroundSize: 'auto, auto, 48px 48px, 48px 48px',
}

// Shared card surface — the same "surf" pattern the roster/canonical-
// surface cards already use (rounded-xl, hairline border, translucent
// dark fill). Centralized here so the command stage + approval queue
// (commit_2) don't each redefine the same object.
export const AGENT_CARD: CSSProperties = {
  borderRadius: 12,
  border: `1px solid ${AGENT_VOID.hairline}`,
  background: 'rgba(14,10,22,0.4)',
  padding: '14px 15px',
}

// Gate accent — derives a status-accent from a raw sq_agent_registry
// default_gate string without hardcoding the vertical-specific vocabulary
// (draft_only / review_required / provider_adapter_gate, ...). Honest
// fallback (unknown) for anything not recognized, rather than guessing.
export function gateAccent(gate: string | null | undefined): string {
  if (!gate) return AGENT_STATUS_ACCENT.unknown
  if (gate.includes('review')) return AGENT_STATUS_ACCENT.violet
  if (gate.includes('draft')) return AGENT_STATUS_ACCENT.cyan
  if (gate.includes('provider')) return AGENT_STATUS_ACCENT.amber
  return AGENT_STATUS_ACCENT.unknown
}

// Agent status accent — active/paused map to the required vocabulary;
// anything else (including null/unrecognized) stays honestly neutral.
export function agentStatusAccent(status: string | null | undefined): string {
  if (status === 'active') return AGENT_STATUS_ACCENT.green
  if (status === 'paused') return AGENT_STATUS_ACCENT.amber
  return AGENT_STATUS_ACCENT.unknown
}

// sq_agent_action_requests status accent — required mapping (Agent
// Runtime Reconciliation, commit_2): pending review is violet,
// approved+executed (or the honest prepared_not_sent terminal state) is
// green, rejected/failed is red, gate/provider blocks are amber. Any
// combination not recognized stays the honest neutral — never a claim
// of known-good or known-bad.
export function agentActionStatusAccent(approvalStatus: string, executionStatus: string): string {
  if (approvalStatus === 'rejected') return AGENT_STATUS_ACCENT.red
  if (executionStatus === 'failed') return AGENT_STATUS_ACCENT.red
  if (executionStatus === 'blocked_on_gate' || executionStatus === 'blocked_provider') return AGENT_STATUS_ACCENT.amber
  if (approvalStatus === 'revision_requested') return AGENT_STATUS_ACCENT.amber
  if (approvalStatus === 'pending_approval') return AGENT_STATUS_ACCENT.violet
  if (approvalStatus === 'approved' && (executionStatus === 'executed' || executionStatus === 'prepared_not_sent')) {
    return AGENT_STATUS_ACCENT.green
  }
  return AGENT_STATUS_ACCENT.unknown
}
